class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onPreviewClick.bind(this));
    this.onHeaderCartIconClick = this.onHeaderCartIconClick.bind(this);
    this.onHeaderCartIconKeydown = this.onHeaderCartIconKeydown.bind(this);
    document.addEventListener('click', this.onHeaderCartIconClick, true);
    document.addEventListener('keydown', this.onHeaderCartIconKeydown, true);
    this.setHeaderCartIconAccessibility();
  }

  onHeaderCartIconClick(event) {
    const cartLink = event.target.closest?.('#cart-icon-bubble');
    if (!cartLink) return;

    event.preventDefault();
    this.open(cartLink);
  }

  onHeaderCartIconKeydown(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;
    const cartLink = event.target.closest?.('#cart-icon-bubble');
    if (!cartLink) return;

    event.preventDefault();
    this.open(cartLink);
  }

  onPreviewClick(event) {
    const trigger = event.target.closest('[data-cart-preview-url]');
    if (!trigger) return;

    const modal = document.querySelector('[data-cart-preview-modal]');
    const image = modal?.querySelector('[data-cart-preview-image]');
    const url = trigger.dataset.cartPreviewUrl;
    if (!modal || !image || !url) return;

    event.preventDefault();
    this.bindPreviewModal(modal, image);
    image.src = url;
    image.alt = trigger.closest('.cart-item')?.querySelector('.cart-item__name')?.textContent.trim() || 'Preview';
    modal.hidden = false;
    document.body.classList.add('cart-preview-modal-open');
    this.previewTrigger = trigger;

    const closeButton = modal.querySelector('.cart-preview-modal__close');
    if (closeButton) trapFocus(modal, closeButton);
  }

  bindPreviewModal(modal, image) {
    if (modal.dataset.bound === 'true') return;

    const close = () => {
      modal.hidden = true;
      document.body.classList.remove('cart-preview-modal-open');
      image.removeAttribute('src');

      const focusTarget = this.previewTrigger?.isConnected ? this.previewTrigger : this.querySelector('.drawer__close');
      this.previewTrigger = null;
      const drawer = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');

      if (this.classList.contains('active') && drawer && focusTarget) {
        trapFocus(drawer, focusTarget);
      } else {
        removeTrapFocus(focusTarget);
      }
    };

    modal.dataset.bound = 'true';
    modal.querySelectorAll('[data-cart-preview-close]').forEach((element) => {
      element.addEventListener('click', close);
    });
    modal.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') close();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    const trapDrawerFocus = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setTimeout(trapDrawerFocus);
    } else {
      this.addEventListener('transitionend', trapDrawerFocus, { once: true });
    }

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
